import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
	Area,
	AreaChart,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from 'recharts'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/integrations/supabase/client'
import {
	format,
	subDays,
	subMonths,
	subYears,
	startOfDay,
	endOfDay,
	isSameDay,
} from 'date-fns'
import TimePeriodSelector from '@/components/dashboard/TimePeriodSelector'
import { useState } from 'react'
import { Skeleton } from '@/components/ui/skeleton'

type TimePeriod = 'all' | 'week' | 'month' | 'year'

interface WidgetTimeSeriesChartProps {
	widgetId: string
}

// Custom hook to handle team data fetching
const useTeamData = () => {
	return useQuery({
		queryKey: ['userTeam'],
		queryFn: async () => {
			// Get the authenticated user
			const {
				data: { user },
				error: authError,
			} = await supabase.auth.getUser()
			if (authError) throw authError
			if (!user) throw new Error('Not authenticated')

			// Get user's teams
			const { data: teams, error: teamsError } = await supabase
				.from('team_members')
				.select('team_id')
				.eq('user_id', user.id)
				.maybeSingle()

			if (teamsError) throw teamsError

			// If user has a team, return it
			if (teams) {
				return { team_id: teams.team_id }
			}

			throw new Error('No team found')
		},
	})
}

const WidgetTimeSeriesChart = ({ widgetId }: WidgetTimeSeriesChartProps) => {
	const [timePeriod, setTimePeriod] = useState<TimePeriod>('week')
	const { data: teamData } = useTeamData()

	const { data: responses, isLoading } = useQuery({
		queryKey: [
			'widget-responses-trend',
			widgetId,
			timePeriod,
			teamData?.team_id,
		],
		queryFn: async () => {
			if (!teamData?.team_id) throw new Error('No team ID found')

			const now = new Date()
			let startDate: Date
			let dateFormat: string
			let groupingFn: (date: Date) => string

			switch (timePeriod) {
				case 'all':
					startDate = new Date(0) // Start from epoch for 'all'
					dateFormat = 'MMM yyyy'
					groupingFn = (date) => format(date, 'yyyy-MM')
					break
				case 'week':
					startDate = subDays(now, 7)
					dateFormat = 'EEE'
					groupingFn = (date) => format(date, 'yyyy-MM-dd')
					break
				case 'month':
					startDate = subMonths(now, 1)
					dateFormat = 'MMM dd'
					groupingFn = (date) => format(date, 'yyyy-MM-dd')
					break
				case 'year':
					startDate = subYears(now, 1)
					dateFormat = 'MMM yyyy'
					groupingFn = (date) => format(date, 'yyyy-MM')
					break
			}

			const { data, error } = await supabase
				.from('responses')
				.select('created_at')
				.eq('widget_id', widgetId)
				.eq('team_id', teamData.team_id)
				.gte('created_at', startDate.toISOString())
				.lte('created_at', now.toISOString())
				.order('created_at', { ascending: true })

			if (error) {
				console.error('Error fetching responses for trend:', error)
				throw error
			}

			// Group responses by the appropriate time period
			const groupedData = data.reduce(
				(acc: { [key: string]: number }, response) => {
					const date = new Date(response.created_at)
					const key = groupingFn(date)
					acc[key] = (acc[key] || 0) + 1
					return acc
				},
				{}
			)

			// Fill in missing dates with zero values
			let current = startDate
			const filledData: { date: string; responses: number }[] = []

			while (current <= now) {
				const key = groupingFn(current)
				const displayDate = format(current, dateFormat)
				filledData.push({
					date: displayDate,
					responses: groupedData[key] || 0,
				})

				if (timePeriod === 'year') {
					current = new Date(current.getFullYear(), current.getMonth() + 1, 1)
				} else {
					current = new Date(current.setDate(current.getDate() + 1))
				}
			}

			return filledData
		},
		enabled: !!widgetId && !!teamData?.team_id,
	})

	if (isLoading) {
		return (
			<Card className="animate-fade-in">
				<CardHeader className="flex flex-row items-center justify-between space-y-0">
					<CardTitle className="text-foreground">Response Trend</CardTitle>
					<TimePeriodSelector
						selectedPeriod={timePeriod}
						onChange={setTimePeriod}
					/>
				</CardHeader>
				<CardContent>
					<div className="h-[300px] w-full">
						<Skeleton className="h-full w-full" />
					</div>
				</CardContent>
			</Card>
		)
	}

	return (
		<Card className="animate-fade-in">
			<CardHeader className="flex flex-row items-center justify-between space-y-0">
				<CardTitle className="text-foreground">Response Trend</CardTitle>
				<TimePeriodSelector
					selectedPeriod={timePeriod}
					onChange={setTimePeriod}
				/>
			</CardHeader>
			<CardContent>
				<div className="h-[300px] w-full">
					<ResponsiveContainer width="100%" height="100%">
						<AreaChart
							data={responses || []}
							margin={{
								top: 20,
								right: 20,
								left: 20,
								bottom: 20,
							}}>
							<defs>
								<linearGradient id="gradient" x1="0" y1="0" x2="0" y2="1">
									<stop offset="0%" stopColor="#866BFE" stopOpacity={0.3} />
									<stop offset="100%" stopColor="#866BFE" stopOpacity={0} />
								</linearGradient>
							</defs>
							<XAxis
								dataKey="date"
								stroke="#94a3b8"
								fontSize={12}
								tickLine={false}
								axisLine={false}
								dy={10}
							/>
							<YAxis
								stroke="#94a3b8"
								fontSize={12}
								tickLine={false}
								axisLine={false}
								tickFormatter={(value) => `${value}`}
								dx={-10}
							/>
							<Tooltip
								content={({ active, payload, label }) => {
									if (active && payload && payload.length) {
										return (
											<div className="rounded-lg border bg-white p-2 shadow-sm dark:bg-gray-800">
												<div className="grid grid-cols-2 gap-2">
													<div className="flex flex-col">
														<span className="text-[0.70rem] uppercase text-muted-foreground">
															Date
														</span>
														<span className="font-bold text-foreground">
															{label}
														</span>
													</div>
													<div className="flex flex-col">
														<span className="text-[0.70rem] uppercase text-muted-foreground">
															Responses
														</span>
														<span className="font-bold text-foreground">
															{payload[0].value}
														</span>
													</div>
												</div>
											</div>
										)
									}
									return null
								}}
							/>
							<Area
								type="monotone"
								dataKey="responses"
								stroke="#866BFE"
								fill="url(#gradient)"
								strokeWidth={2}
								dot={{
									r: 4,
									fill: '#866BFE',
									strokeWidth: 2,
								}}
								activeDot={{
									r: 6,
									fill: '#866BFE',
									strokeWidth: 2,
								}}
							/>
						</AreaChart>
					</ResponsiveContainer>
				</div>
			</CardContent>
		</Card>
	)
}

export default WidgetTimeSeriesChart
